import React, { useState, useMemo } from 'react';
import { Mafs, Coordinates, Plot, Point, Line, Polygon, Text, Vector, Theme, MovablePoint, Circle } from 'mafs';
import * as math from 'mathjs';

export const CartesianGraph = ({ config, variables }) => {
    // State for interactive features
    const [draggedPoints, setDraggedPoints] = useState({});
    const [hoverCoords, setHoverCoords] = useState(null);

    // View configuration
    const viewBox = config.viewBox || { x: [-5, 5], y: [-5, 5] };
    const grid = config.grid || {};
    const interactive = config.interactive || {};

    // Merge dragged points with original points
    const points = useMemo(() => {
        return (config.points || []).map((point, i) => ({
            ...point,
            x: draggedPoints[i]?.x ?? point.x,
            y: draggedPoints[i]?.y ?? point.y
        }));
    }, [config.points, draggedPoints]);

    // Snap to grid helper
    const snapToGrid = ([x, y], spacing) => {
        return [
            Math.round(x / spacing) * spacing,
            Math.round(y / spacing) * spacing
        ];
    };

    const renderPoints = () => {
        return points.map((point, i) => (
            <React.Fragment key={`point-${i}`}>
                {point.draggable && interactive.allowPointDrag ? (
                    <MovablePoint
                        point={[point.x, point.y]}
                        color={point.color || Theme.blue}
                        onMove={(newPos) => {
                            const snappedPos = interactive.snapToGrid
                                ? snapToGrid(newPos, interactive.gridSnapSpacing || 1)
                                : newPos;
                            setDraggedPoints(prev => ({
                                ...prev,
                                [i]: { x: snappedPos[0], y: snappedPos[1] }
                            }));
                        }}
                    />
                ) : (
                    <Point
                        x={point.x}
                        y={point.y}
                        color={point.color || Theme.blue}
                    />
                )}

                {point.label && (
                    <Text
                        x={point.x + 0.3}
                        y={point.y + 0.3}
                        color={point.color || Theme.blue}
                    >
                        {point.label}({point.x.toFixed(1)}, {point.y.toFixed(1)})
                    </Text>
                )}
            </React.Fragment>
        ));
    };

    const renderSegments = () => {
        return (config.segments || []).map((seg, i) => {
            const [x1, y1] = seg.point1;
            const [x2, y2] = seg.point2;
            const length = Math.sqrt((x2-x1)**2 + (y2-y1)**2);
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;

            return (
                <React.Fragment key={`seg-${i}`}>
                    {seg.arrows && seg.arrows !== "none" ? (
                        <Vector
                            tail={[x1, y1]}
                            tip={[x2, y2]}
                            color={seg.color || Theme.blue}
                            weight={seg.weight || 2}
                        />
                    ) : (
                        <Line.Segment
                            point1={[x1, y1]}
                            point2={[x2, y2]}
                            color={seg.color || Theme.blue}
                            weight={seg.weight || 2}
                            style={seg.style || "solid"}
                        />
                    )}

                    {seg.showLength && (
                        <Text x={midX} y={midY + 0.3} size={12}>
                            d = {length.toFixed(2)}
                        </Text>
                    )}

                    {seg.showMidpoint && (
                        <Point x={midX} y={midY} color={seg.color || Theme.blue} />
                    )}

                    {seg.label && (
                        <Text x={midX - 0.3} y={midY - 0.3}>
                            {seg.label}
                        </Text>
                    )}
                </React.Fragment>
            );
        });
    };

    const renderFunctions = () => {
        return (config.functions || []).map((fn, i) => {
            try {
                const compiled = math.compile(fn.expression);
                const evaluate = (x) => {
                    if (fn.domain) {
                        const [minX, maxX] = fn.domain;
                        if (x < minX || x > maxX) return NaN;
                    }
                    return compiled.evaluate({ x, ...variables });
                };

                evaluate(0);

                return (
                    <React.Fragment key={`fn-${i}`}>
                        <Plot.OfX
                            y={evaluate}
                            color={fn.color || Theme.blue}
                            style={fn.style || 'solid'}
                            weight={fn.weight || 3}
                        />
                        {fn.label && (
                            <Text
                                x={viewBox.x[1] - 1}
                                y={evaluate(viewBox.x[1] - 1) + 0.5}
                                color={fn.color || Theme.blue}
                            >
                                {fn.label}
                            </Text>
                        )}
                    </React.Fragment>
                );
            } catch (e) {
                return (
                    <Text key={`fn-error-${i}`} x={0} y={0} color="red">
                        Error in function {i+1}: {fn.expression}
                    </Text>
                );
            }
        });
    };

    const renderRegions = () => {
        return (config.regions || []).map((region, i) => {
            if (region.type === "polygon") {
                return (
                    <Polygon
                        key={`region-${i}`}
                        points={region.points}
                        color={region.strokeColor || Theme.blue}
                        fillOpacity={region.fillOpacity || 0.2}
                        strokeWidth={region.strokeWidth || 2}
                    />
                );
            } else if (region.type === "circle") {
                return (
                    <Circle
                        key={`region-${i}`}
                        center={region.center}
                        radius={region.radius}
                        color={region.strokeColor || Theme.blue}
                        fillOpacity={region.fillOpacity || 0.2}
                    />
                );
            }
            return null;
        });
    };

    const renderStudentComparison = () => {
        if (!config.studentGraph) return null;

        const studentColor = "#fa8c16"; // Orange

        return (
            <React.Fragment>
                {(config.studentGraph.points || []).map((point, i) => (
                    <React.Fragment key={`student-point-${i}`}>
                        <Point x={point.x} y={point.y} color={studentColor} />
                        {point.label && (
                            <Text x={point.x + 0.3} y={point.y - 0.3} color={studentColor}>
                                {point.label}' ({point.x.toFixed(1)}, {point.y.toFixed(1)})
                            </Text>
                        )}
                    </React.Fragment>
                ))}

                {(config.studentGraph.functions || []).map((fn, i) => {
                    try {
                        const compiled = math.compile(fn.expression);
                        return (
                            <Plot.OfX
                                key={`student-fn-${i}`}
                                y={(x) => compiled.evaluate({ x, ...variables })}
                                color={studentColor}
                                style="dashed"
                                weight={2}
                            />
                        );
                    } catch (e) {
                        return null;
                    }
                })}
            </React.Fragment>
        );
    };

    return (
        <div className="ch-cartesian-graph">
            <Mafs
                viewBox={viewBox}
                pan={false}
                onMouseMove={(point) => {
                    if (interactive.showCoordinatesOnHover) {
                        setHoverCoords(point);
                    }
                }}
            >
                <Coordinates.Cartesian
                    xAxis={{
                        lines: grid.spacing || 1,
                        labels: grid.showNumbers !== false ? (x) => `${x}` : false
                    }}
                    yAxis={{
                        lines: grid.spacing || 1,
                        labels: grid.showNumbers !== false ? (y) => `${y}` : false
                    }}
                />

                {renderRegions()}
                {renderFunctions()}
                {renderStudentComparison()}
                {renderSegments()}
                {renderPoints()}

                {hoverCoords && (
                    <Text x={hoverCoords[0] + 0.5} y={hoverCoords[1] + 0.5} size={12}>
                        ({hoverCoords[0].toFixed(2)}, {hoverCoords[1].toFixed(2)})
                    </Text>
                )}
            </Mafs>

            {config.showLegend && config.legend && (
                <div className="ch-graph-legend">
                    {config.legend.map((item, i) => (
                        <div key={i} className="ch-legend-item">
              <span
                  className="ch-legend-color"
                  style={{ backgroundColor: item.color, display: 'inline-block', width: '12px', height: '12px', marginRight: '8px' }}
              />
                            <span>{item.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};